import { cloneDeep } from 'lodash';
import { rgbaToHexa } from '@meitu/saas-colors';
// 初始化文字需要的依赖
import { createCanvas, fixZipResUrl, waterMarkBridge } from '@meitu/wasm-lib';
import {
    FULL_SCREEN_TYPE,
    getDefaultWatermarkLayerData,
    IExportPlugin,
    IMAGE_LAYER_CANVAS_ID_PREFIX,
    IWatermarkLayerProps,
    LayerType,
    MaterialIdsType,
    PluginType,
    WATERMARK_DEFAULT_CROSS,
    WATERMARK_DEFAULT_SIZE,
    WATERMARK_DEFAULT_SPACING,
    WATERMARK_TEXTS_MAX,
    WATERMARK_TYPE,
    type ILayerProps,
} from '@/version-types';
import { hexToRgbaArr } from '../../utils/color-kit';
import { Layer } from '../layer';
import watermarkLayerBehavior from './behavior';
import { AR_RES_ID, AR_RES_PATH, WATERMARK_HISTORY_KEY } from './constants';
import watermarkLayerState, { getWatermarkLayerState } from './states/watermark-state';
import {
    clearTextWatermark,
    computedPosition,
    createWatermark,
    getWatermarkImg,
    registerFont,
    setImgWatermark,
    setTextWatermark,
} from './utils/watermark';
import { WatermarkLayerHistory } from './watermark-history';

export class WatermarkLayer extends Layer {
    private state: boolean;
    private initLoading: boolean;
    private fullScreenChangedEvent = 'watermark-fullscreen-changed-event';
    private initPromise: Promise<boolean> | null;

    constructor(identity: string) {
        super(identity);
        this.state = false;
        this.initLoading = false;
        this.initPromise = null;
    }

    destroy() {
        waterMarkBridge.destroy();
        this.state = false;
        getWatermarkLayerState(this.identity).setBridgeState(false);
    }

    async changeTextWatermark(id: string, texts: any[], rgba: number[], fontFamily: string, update = false) {
        await clearTextWatermark(id);
        for (let i = 0; i < texts.length; i++) {
            const text = texts[i];
            await waterMarkBridge.addText(id, text);
            await waterMarkBridge.setTextColor(id, rgba[0], rgba[1], rgba[2], rgba[3], i);
            await waterMarkBridge.setTextFont(id, fontFamily, i);
        }
        if (update) {
            await waterMarkBridge.updateResultBuffer(id);
            const layer = this.framework.getLayer(id)!;
            const oldLayer = cloneDeep(layer);

            const newLayer = this.framework.getLayer(layer.id, true);
            const result = await getWatermarkImg(id);
            Object.assign(newLayer.data, {
                url: result?.url || '',
                texts,
            });

            this.framework.stage.addOrUpdate(newLayer);
            this.framework.history.write({
                type: WATERMARK_HISTORY_KEY,
                data: {
                    oldLayer,
                    newLayer,
                    updateBridge: this.updateBridge.bind(this),
                },
            });
        }
        return true;
    }

    async runWatermarkAndGetResult(
        stageIndex: number,
        watermarkLayer: ILayerProps,
        stageSize: { width: number; height: number },
    ) {
        const { data, materialIds, rotate } = watermarkLayer;
        const { originUrl, isFullScreen, zipUrl, size, space, cross, offsetX, offsetY } = data as IWatermarkLayerProps;
        const materialId = materialIds?.[MaterialIdsType.WATERMARK]?.[0];
        await this.initWatermarkImageKit(stageIndex);
        await createWatermark(watermarkLayer.id);
        // 不设置字体,默认为图片水印
        if (!materialId && originUrl) {
            await setImgWatermark(watermarkLayer.id, originUrl);
        } else {
            await setTextWatermark(watermarkLayer.id, zipUrl);
            await this.initTextWatermark(watermarkLayer.id, watermarkLayer);
        }
        if (isFullScreen) {
            await waterMarkBridge.setFullscreen(watermarkLayer.id, true);

            await waterMarkBridge.setWidthRatio(watermarkLayer.id, size, 0);
            await waterMarkBridge.setSpacing(watermarkLayer.id, space, 0);
            await waterMarkBridge.setRotate(watermarkLayer.id, rotate, 0);
            await waterMarkBridge.setStaggered(watermarkLayer.id, cross, 0);
            await waterMarkBridge.setMoveOffsetX(watermarkLayer.id, offsetX);
            await waterMarkBridge.setMoveOffsetY(watermarkLayer.id, offsetY);
        }

        const result = (await getWatermarkImg(watermarkLayer.id)) as {
            url: string;
            width: number;
            height: number;
        };

        return result;
    }

    async batchChangeTextWatermark(
        texts: string[],
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.getStages();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const newLayer = cloneDeep(watermarkLayer);
            (newLayer.data as IWatermarkLayerProps).texts = texts;
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer, {
                width: bgLayer.width,
                height: bgLayer.height,
            });
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.stage.addOrUpdate(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.history.write(oldLayers, newLayers);
    }

    async batchAddWatermarkLayer(
        type: WATERMARK_TYPE,
        stageIndexList: number[],
        url?: string,
        textProps?: {
            materialId: string;
            zipUrl: string;
        },
        isFullScreen?: boolean,
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.getStages();
        const addLayers: ILayerProps[] = [];
        const newLayers: ILayerProps[] = [];
        const oldLayers: ILayerProps[] = [];
        this.framework.history.pushFunc('18_88');
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK)!;
            oldLayers.push(cloneDeep(watermarkLayer));
            const layer = getDefaultWatermarkLayerData(type, url, textProps);
            if (watermarkLayer) {
                layer.id = watermarkLayer.id;
            }

            if (isFullScreen) {
                Object.assign(layer, {
                    width: bgLayer.width,
                    height: bgLayer.height,
                    top: 0,
                    left: 0,
                    rotate: 0,
                    data: {
                        ...layer.data,
                        ...{
                            isFullScreen: true,
                            size: 47 / 100,
                            space: WATERMARK_DEFAULT_SPACING / 100,
                            cross: WATERMARK_DEFAULT_CROSS / 100,
                        },
                    },
                });
            }

            const result = await this.runWatermarkAndGetResult(stageIndex, layer, {
                width: bgLayer.width,
                height: bgLayer.height,
            });
            (layer.data as IWatermarkLayerProps).url = result?.url!;

            if (!isFullScreen) {
                Object.assign(
                    layer,
                    computedPosition(
                        { width: result.width, height: result.height },
                        {
                            width: bgLayer.width,
                            height: bgLayer.height,
                        },
                    ),
                );
            }
            if (!watermarkLayer) {
                addLayers.push(layer);
            } else {
                newLayers.push(cloneDeep(layer));
                this.framework.stage.addOrUpdate(layer, undefined, stageIndex);
            }
            await this.checkBatchImageKit(layer.id, stageIndex);
            setStageLoading?.(stageIndex, false);
        }

        return addLayers;
    }

    async batchSetWatermarkFullScreen(
        type: FULL_SCREEN_TYPE,
        value: number,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.getStages();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const newLayer = cloneDeep(watermarkLayer);
            if (type === FULL_SCREEN_TYPE.SIZE) {
                (newLayer?.data as IWatermarkLayerProps).size = value / 100;
            } else if (type === FULL_SCREEN_TYPE.SPACE) {
                (newLayer.data as IWatermarkLayerProps).space = value / 100;
            } else if (type === FULL_SCREEN_TYPE.ROTATE) {
                newLayer.rotate = value;
            } else if (type === FULL_SCREEN_TYPE.CROSS) {
                (newLayer.data as IWatermarkLayerProps).cross = value / 100;
            } else if (type === FULL_SCREEN_TYPE.OFFSET_X) {
                (newLayer.data as IWatermarkLayerProps).offsetX = value / 100;
            } else if (type === FULL_SCREEN_TYPE.OFFSET_Y) {
                (newLayer.data as IWatermarkLayerProps).offsetY = value / 100;
            }
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer, {
                width: bgLayer.width,
                height: bgLayer.height,
            });
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.stage.addOrUpdate(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.history.write(oldLayers, newLayers);
    }

    async batchSetWatermarkPosition(
        position: string,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.getStages();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            const stageSize = {
                width: bgLayer.width,
                height: bgLayer.height,
            };
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const newLayer = cloneDeep(watermarkLayer);
            const spaceSize = {
                width: 0.1 * stageSize.width,
                height: 0.1 * stageSize.height,
            };
            if (position === 'top-left') {
                Object.assign(newLayer, {
                    top: spaceSize.height,
                    left: spaceSize.width,
                });
            } else if (position === 'top-center') {
                Object.assign(newLayer, {
                    top: spaceSize.height,
                    left: stageSize.width / 2 - watermarkLayer.width / 2,
                });
            } else if (position === 'top-right') {
                Object.assign(newLayer, {
                    top: spaceSize.height,
                    left: stageSize.width - watermarkLayer.width - spaceSize.width,
                });
            } else if (position === 'center-left') {
                Object.assign(newLayer, {
                    top: stageSize.height / 2 - watermarkLayer.height / 2,
                    left: spaceSize.width,
                });
            } else if (position === 'center-center') {
                Object.assign(newLayer, {
                    top: stageSize.height / 2 - watermarkLayer.height / 2,
                    left: stageSize.width / 2 - watermarkLayer.width / 2,
                });
            } else if (position === 'center-right') {
                Object.assign(newLayer, {
                    top: stageSize.height / 2 - watermarkLayer.height / 2,
                    left: stageSize.width - watermarkLayer.width - spaceSize.width,
                });
            } else if (position === 'bottom-left') {
                Object.assign(newLayer, {
                    top: stageSize.height - watermarkLayer.height - spaceSize.height,
                    left: spaceSize.width,
                });
            } else if (position === 'bottom-center') {
                Object.assign(newLayer, {
                    top: stageSize.height - watermarkLayer.height - spaceSize.height,
                    left: stageSize.width / 2 - watermarkLayer.width / 2,
                });
            } else if (position === 'bottom-right') {
                Object.assign(newLayer, {
                    top: stageSize.height - watermarkLayer.height - spaceSize.height,
                    left: stageSize.width - watermarkLayer.width - spaceSize.width,
                });
            }

            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.stage.addOrUpdate(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.history.write(oldLayers, newLayers);
    }

    async batchSetTextWatermarkColor(
        { r, g, b, a },
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.getStages();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const { id } = watermarkLayer;
            const newLayer = cloneDeep(watermarkLayer);
            const { texts } = newLayer.data as IWatermarkLayerProps;

            for (let i = 0; i < texts.length; i++) {
                await waterMarkBridge.setTextColor(id, r, g, b, a, i);
            }
            Object.assign(newLayer.data, {
                textColor: rgbaToHexa({
                    r,
                    g,
                    b,
                    a: a / 100,
                }),
            });
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer, {
                width: bgLayer.width,
                height: bgLayer.height,
            });
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.stage.addOrUpdate(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.history.write(oldLayers, newLayers);
    }

    async batchSetOpacity(
        opacity: number,
        stageIndexList: number[],
        originOpacity?: number | null,
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.getStages();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const newLayer = cloneDeep(watermarkLayer);
            newLayer.opacity = opacity;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.stage.addOrUpdate(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.history.write(oldLayers, newLayers);
    }

    async batchSetWatermarkFont(
        fontFamily: string,
        fontFamilyId: number,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.getStages();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            setStageLoading?.(stageIndex, true);
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            const newLayer = cloneDeep(watermarkLayer);
            (newLayer.data as IWatermarkLayerProps).fontFamily = fontFamily;
            await this.loadTextFamily(fontFamily);
            (newLayer.data as IWatermarkLayerProps).fontFamily = fontFamily;
            const materialIds = newLayer.materialIds || {};
            materialIds[MaterialIdsType.FONT_FAMILY] = [String(fontFamilyId)];
            newLayer.materialIds = materialIds;
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer, {
                width: bgLayer.width,
                height: bgLayer.height,
            });
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.stage.addOrUpdate(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.history.write(oldLayers, newLayers);
    }

    async batchRemoveWatermarkLayer(stageIndexList: number[]) {
        const stageList = this.framework.getStages();
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const layers = this.framework.getLayers(stageIndex);
            for (let j = 0; j < layers.length; j++) {
                const layer = layers[j];
                if (layer.layerType === LayerType.WATERMARK) {
                    await waterMarkBridge.removeWaterMark(layer.id);
                    this.framework.operate.delLayer(layer.id);
                }
            }
        }
    }

    async setBatchFullScreen(
        isFullScreen: boolean,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.getStages();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            setStageLoading?.(stageIndex, true);
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            const mainLayer = stageList[i].find((layer) => layer.layerType === LayerType.MAIN_LAYER)!;
            if (!watermarkLayer) continue;
            const newLayer = cloneDeep(watermarkLayer);
            (newLayer.data as IWatermarkLayerProps).isFullScreen = isFullScreen;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            const { width, height } = mainLayer;
            if (isFullScreen) {
                Object.assign(newLayer, {
                    width,
                    height,
                    top: 0,
                    left: 0,
                    rotate: 0,
                    data: {
                        ...newLayer.data,
                        ...{
                            url: '',
                            isFullScreen: true,
                            size: 47 / 100,
                            space: WATERMARK_DEFAULT_SPACING / 100,
                            cross: WATERMARK_DEFAULT_CROSS / 100,
                        },
                    },
                });
            } else {
                // 设置非全屏
                Object.assign(newLayer, {
                    rotate: 0,
                    data: {
                        ...newLayer.data,
                        ...{
                            url: '',
                            size: 0,
                            space: 0,
                            rotate: 0,
                            cross: 0,
                        },
                    },
                });
            }
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer, {
                width: bgLayer.width,
                height: bgLayer.height,
            });
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            if (!isFullScreen) {
                Object.assign(
                    newLayer,
                    computedPosition({ width: result!.width, height: result!.height }, { width, height }),
                    {
                        rotate: 0,
                    },
                );
            }
            // const { id } = watermarkLayer;
            // const result = await getWatermarkImg(id);
            // if (!result) {
            //     await this.checkBatchImageKit(id, stageIndex);
            // }
            // // 设置全屏
            // await waterMarkBridge.setFullscreen(id, isFullScreen);
            // const mainLayer = stageList[i].find((layer) => layer.layerType === LayerType.MAIN_LAYER)!;

            // const { width, height } = mainLayer;
            // const stageSize = {
            //     width,
            //     height,
            // };
            // if (isFullScreen) {
            //     await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.ROTATE, watermarkLayer.rotate, false);
            //     await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.SIZE, 50, false);
            //     await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.SPACE, WATERMARK_DEFAULT_SPACING, false);
            //     await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.CROSS, WATERMARK_DEFAULT_CROSS, false);
            //     const result = await getWatermarkImg(id);
            //     Object.assign(newLayer, {
            //         width,
            //         height,
            //         top: 0,
            //         left: 0,
            //         rotate: watermarkLayer.rotate,
            //         data: {
            //             ...newLayer.data,
            //             ...{
            //                 url: result?.url || '',
            //                 size: WATERMARK_DEFAULT_SIZE / 100,
            //                 space: WATERMARK_DEFAULT_SPACING / 100,
            //                 cross: WATERMARK_DEFAULT_CROSS / 100,
            //             },
            //         },
            //     });
            // } else {
            //     await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.ROTATE, 0, false);
            //     const result = await getWatermarkImg(id);
            //     // 设置非全屏
            //     Object.assign(newLayer, computedPosition({ width: result!.width, height: result!.height }, stageSize), {
            //         rotate: 0,
            //         data: {
            //             ...newLayer.data,
            //             ...{
            //                 url: result?.url || '',
            //                 size: 0,
            //                 space: 0,
            //                 rotate: 0,
            //                 cross: 0,
            //             },
            //         },
            //     });
            // }

            this.framework.stage.addOrUpdate(newLayer);
            setStageLoading?.(stageIndex, false);
        }

        this.framework.history.write(oldLayers, newLayers);
    }

    async batchUpdateBase(
        size: number,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.getStages();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            setStageLoading?.(stageIndex, true);
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            if (!watermarkLayer) continue;
            oldLayers.push(cloneDeep(watermarkLayer));
            const newLayer = cloneDeep(watermarkLayer);
            (newLayer.data as IWatermarkLayerProps).size = size;
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer, {
                width: bgLayer.width,
                height: bgLayer.height,
            });
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            newLayers.push(cloneDeep(newLayer));
            this.framework.stage.addOrUpdate(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.history.write(oldLayers, newLayers);
    }

    async setTextWatermarkColor(id: string, { r, g, b, a }, count: number, update = false) {
        for (let i = 0; i < count; i++) {
            await waterMarkBridge.setTextColor(id, r, g, b, a, i);
        }
        if (update) {
            await waterMarkBridge.updateResultBuffer(id);
            const layer = this.framework.getLayer(id)!;
            const oldLayer = cloneDeep(layer);
            const newLayer = this.framework.getLayer(layer.id, true)!;
            const result = await getWatermarkImg(id);
            Object.assign(newLayer.data, {
                url: result?.url || '',
                textColor: rgbaToHexa({
                    r,
                    g,
                    b,
                    a: a / 100,
                }),
            });

            this.framework.stage.addOrUpdate(newLayer);
            this.framework.history.write({
                type: WATERMARK_HISTORY_KEY,
                data: {
                    oldLayer,
                    newLayer,
                    updateBridge: this.updateBridge.bind(this),
                },
            });
        }
    }

    async setWatermarkFullScreen(id: string, type: FULL_SCREEN_TYPE, value: number, isHistory = true) {
        await this.checkImageKit(id);
        const layer = this.framework.getLayer(id);
        if (!layer) return;

        const oldLayer = cloneDeep(layer);

        const newLayer = this.framework.getLayer(layer.id, true)!;
        if (type === FULL_SCREEN_TYPE.SIZE) {
            // (newLayer?.data as IWatermarkLayerProps).size = value / 100;
            await waterMarkBridge.setWidthRatio(id, value / 100, 0);
        } else if (type === FULL_SCREEN_TYPE.SPACE) {
            (newLayer.data as IWatermarkLayerProps).space = value / 100;
            await waterMarkBridge.setSpacing(id, value / 100.0, 0);
        } else if (type === FULL_SCREEN_TYPE.ROTATE) {
            newLayer.rotate = value;
            await waterMarkBridge.setRotate(id, value, 0);
        } else if (type === FULL_SCREEN_TYPE.CROSS) {
            (newLayer.data as IWatermarkLayerProps).cross = value / 100;
            await waterMarkBridge.setStaggered(id, value / 100.0, 0);
        } else if (type === FULL_SCREEN_TYPE.OFFSET_X) {
            (newLayer.data as IWatermarkLayerProps).offsetX = value / 100;
            await waterMarkBridge.setMoveOffsetX(id, value / 100.0);
        } else if (type === FULL_SCREEN_TYPE.OFFSET_Y) {
            (newLayer.data as IWatermarkLayerProps).offsetY = value / 100;
            await waterMarkBridge.setMoveOffsetY(id, value / 100.0);
        }
        if (isHistory) {
            const result = await getWatermarkImg(id);
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;

            this.framework.stage.addOrUpdate(newLayer);
            this.framework.history.write({
                type: WATERMARK_HISTORY_KEY,
                data: {
                    oldLayer,
                    newLayer,
                    updateBridge: this.updateBridge.bind(this),
                },
            });
        }
        return true;
    }

    async checkImageKit(id?: string, index?: number) {
        if (!this.state) {
            await this.initWatermarkImageKit(index);
        }
        if (id) {
            const result = await getWatermarkImg(id);
            if (result) return;
            await createWatermark(id);
            const layer = this.framework.getLayer(id) as ILayerProps;
            this.updateBridge(layer);
        }
        return true;
    }

    async checkBatchImageKit(id?: string, index?: number) {
        const mainLayer = this.framework.getMainLayer(index === undefined ? 0 : index)!;
        try {
            if (id) {
                const result = await getWatermarkImg(id);
                if (result) return;
                const { width, height } = mainLayer;
                const originCanvas = createCanvas(width, height);
                await waterMarkBridge.from(originCanvas).create(AR_RES_ID, fixZipResUrl(AR_RES_PATH));
                getWatermarkLayerState(this.identity).setBridgeState(true);
                await createWatermark(id);
                const layer = this.framework.getLayer(id) as ILayerProps;
                this.updateBridge(layer);
            }
            return true;
        } catch (error) {
            console.error('水印工具初始化失败', error);
            return false;
        } finally {
            this.initLoading = false;
        }
        return true;
    }

    private async loadTextFamily(fontFamily: string) {
        const fontList = this.framework.getFontList();
        const font = fontList.find((v) => v.font_name === fontFamily);
        if (font) {
            const { zip_file, old_zip_file } = font as any;
            const url = zip_file || old_zip_file;
            if (!url) throw new Error('Font zip url can not be empty');
            await registerFont(fontFamily, url);
        } else {
            // demo加载默认斗鱼字体
            const zipFile = await import(`./assets/douyufont.zip`);
            await registerFont('douyufont', zipFile.default);
        }
    }

    // 把配置同步到底层
    async updateBridge(layer: ILayerProps) {
        const { rotate, id } = layer;
        const { fontFamily, originUrl, textColor, isFullScreen, zipUrl, space, cross } =
            layer.data as IWatermarkLayerProps;
        if (!fontFamily && !textColor) {
            await setImgWatermark(layer.id, originUrl);
        } else {
            await setTextWatermark(layer.id, zipUrl);
            await this.initTextWatermark(layer.id, layer);
        }
        if (isFullScreen) {
            await waterMarkBridge.setFullscreen(id, isFullScreen);
            await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.CROSS, cross * 100, false);
            await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.SIZE, 50, false);
            await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.SPACE, space * 100, false);
            await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.ROTATE, rotate, false);
        }
        return true;
    }

    private async initImageKit(index?: number) {
        this.initLoading = true;

        const originIndex = index !== undefined ? index : this.framework.stage.getActiveOriginIndex();
        const mainLayer = this.framework.getMainLayer(originIndex)!;

        const { width, height } = mainLayer;
        const originCanvas = createCanvas(width, height);
        console.log('====>originCanvas', width, height);
        try {
            await waterMarkBridge.from(originCanvas).create(AR_RES_ID, fixZipResUrl(AR_RES_PATH));
            this.state = true;
            getWatermarkLayerState(this.identity).setBridgeState(true);
            return true;
        } catch (error) {
            console.error('水印工具初始化失败', error);
            return false;
        } finally {
            this.initLoading = false;
        }
    }
    async initWatermarkImageKit(index?: number) {
        if (this.state && index === undefined) return true;
        // 正常初始化
        if (!this.initLoading) {
            this.initPromise = this.initImageKit(index);
        }

        return this.initPromise as Promise<boolean>;
    }

    private async initTextWatermark(id: string, layer: ILayerProps) {
        let fontFamily = (layer.data as IWatermarkLayerProps).fontFamily;
        let texts: string[] = [];
        let rgba: number[] = [];
        if (fontFamily) {
            texts = (layer.data as IWatermarkLayerProps).texts;
            rgba = hexToRgbaArr((layer.data as IWatermarkLayerProps).textColor) as number[];
        } else {
            const info = await waterMarkBridge.getTextsInfo(id);
            if (info == null || info.textLayers == undefined) return;
            // 默认文本长度
            const count = info.textLayers.length;
            if (count == 0) return;
            fontFamily = info.textLayers[0].info.mTextFont;
            texts = info.textLayers.map((v) => v.info.mTextString);
            rgba = [
                info.textLayers[0].info.mTextORgba.r * 255,
                info.textLayers[0].info.mTextORgba.g * 255,
                info.textLayers[0].info.mTextORgba.b * 255,
                info.textLayers[0].info.mTextORgba.a,
            ];
            Object.assign(layer.data, {
                texts,
                textColor: rgbaToHexa({
                    r: info.textLayers[0].info.mTextORgba.r * 255,
                    g: info.textLayers[0].info.mTextORgba.g * 255,
                    b: info.textLayers[0].info.mTextORgba.b * 255,
                    a: info.textLayers[0].info.mTextORgba.a,
                }),
                fontFamily,
            });
        }
        await this.loadTextFamily(fontFamily);
        await getWatermarkImg(id);
        await this.changeTextWatermark(id, texts, rgba, fontFamily);
        await this.setTextWatermarkColor(
            id,
            {
                r: rgba[0],
                g: rgba[1],
                b: rgba[2],
                a: rgba[3] * 100,
            },
            texts.length,
        );
        await waterMarkBridge.updateResultBuffer(id);
        return true;
    }

    async addWatermarkLayer(
        type: WATERMARK_TYPE,
        stageSize: {
            width: number;
            height: number;
        },
        url?: string,
        textProps?: {
            materialId: string;
            zipUrl: string;
        },
        isFullScreen?: boolean,
        update?: boolean,
    ) {
        const oldLayer = cloneDeep(this.getWatermarkLayer());
        const layer = getDefaultWatermarkLayerData(type, url, textProps);
        // TODO:替换逻辑,id要同步为一个
        const watermarkLayer = this.getWatermarkLayer();
        if (watermarkLayer) {
            layer.id = watermarkLayer.id;
        }
        await this.initWatermarkImageKit();
        await createWatermark(layer.id);
        // 不设置字体,默认为图片水印
        if (!textProps?.materialId && url) {
            await setImgWatermark(layer.id, url);
        } else {
            await setTextWatermark(layer.id, textProps?.zipUrl!);
            await this.initTextWatermark(layer.id, layer);
        }
        if (isFullScreen) {
            await waterMarkBridge.setFullscreen(layer.id, true);
            await this.setWatermarkFullScreen(layer.id, FULL_SCREEN_TYPE.ROTATE, 0, false);
            await this.setWatermarkFullScreen(layer.id, FULL_SCREEN_TYPE.SPACE, WATERMARK_DEFAULT_SPACING, false);
            await this.setWatermarkFullScreen(layer.id, FULL_SCREEN_TYPE.CROSS, WATERMARK_DEFAULT_CROSS, false);
            await this.setWatermarkFullScreen(layer.id, FULL_SCREEN_TYPE.SIZE, 47, false);
            Object.assign(layer, {
                width: stageSize.width,
                height: stageSize.height,
                top: 0,
                left: 0,
                rotate: 0,
                data: {
                    ...layer.data,
                    ...{
                        isFullScreen: true,
                        size: 47 / 100,
                        space: WATERMARK_DEFAULT_SPACING / 100,
                        cross: WATERMARK_DEFAULT_CROSS / 100,
                    },
                },
            });
        }
        if (update) {
            const result = (await getWatermarkImg(layer.id)) as {
                url: string;
                width: number;
                height: number;
            };
            (layer.data as IWatermarkLayerProps).url = result?.url!;
            if (!isFullScreen) {
                Object.assign(layer, computedPosition({ width: result.width, height: result.height }, stageSize));
            }

            let newLayer: ILayerProps;
            if (!watermarkLayer) {
                this.framework.stage.addOrUpdate(layer);
                newLayer = layer;
            } else {
                this.framework.stage.addOrUpdate(layer);
                newLayer = this.framework.getLayer(layer.id)!;
            }

            if (!isFullScreen) {
                this.framework.operate.setActiveLayer(newLayer.id);
            }
            this.framework.history.write({
                type: WATERMARK_HISTORY_KEY,
                data: {
                    oldLayer,
                    newLayer,
                    updateBridge: this.updateBridge.bind(this),
                },
                trackFunc: '18_88',
            });
            await this.checkImageKit(newLayer.id);
        }

        this.emitFullScreenChanged(!!isFullScreen);
    }

    async setFullScreen(isFullScreen: boolean, stageSize: { width: number; height: number }, update = false) {
        const originIndex = this.framework.stage.getActiveOriginIndex();
        const layers = this.framework.getLayers(originIndex);

        let watermarkLayer = layers.filter((layer) => layer.layerType === LayerType.WATERMARK)[0];
        if (!watermarkLayer) return;

        watermarkLayer = this.framework.getLayer(watermarkLayer.id)!;
        const { id } = watermarkLayer;
        await this.checkImageKit(id);
        const oldLayer = cloneDeep(watermarkLayer);
        const newLayer = this.framework.getLayer(watermarkLayer.id, true)!;
        (newLayer.data as IWatermarkLayerProps).isFullScreen = isFullScreen;
        // 设置全屏
        await waterMarkBridge.setFullscreen(id, isFullScreen);
        if (isFullScreen) {
            const originIndex = this.framework.stage.getActiveOriginIndex();
            const mainLayer = this.framework.getMainLayer(originIndex)!;

            const { width, height } = mainLayer;
            await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.ROTATE, watermarkLayer.rotate, false);
            await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.SIZE, 50, false);
            await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.SPACE, WATERMARK_DEFAULT_SPACING, false);
            await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.CROSS, WATERMARK_DEFAULT_CROSS, false);
            const result = await getWatermarkImg(id);
            Object.assign(newLayer, {
                width,
                height,
                top: 0,
                left: 0,
                rotate: watermarkLayer.rotate,
                data: {
                    ...newLayer.data,
                    ...{
                        url: result?.url || '',
                        size: WATERMARK_DEFAULT_SIZE / 100,
                        space: WATERMARK_DEFAULT_SPACING / 100,
                        cross: WATERMARK_DEFAULT_CROSS / 100,
                    },
                },
            });
        } else {
            await this.setWatermarkFullScreen(id, FULL_SCREEN_TYPE.ROTATE, 0, false);
            const result = await getWatermarkImg(id);
            // 设置非全屏
            Object.assign(newLayer, computedPosition({ width: result!.width, height: result!.height }, stageSize), {
                rotate: 0,
                data: {
                    ...newLayer.data,
                    ...{
                        url: result?.url || '',
                        size: 0,
                        space: 0,
                        rotate: 0,
                        cross: 0,
                    },
                },
            });
        }
        if (update) {
            this.framework.stage.addOrUpdate(newLayer);
            this.framework.history.write({
                type: WATERMARK_HISTORY_KEY,
                data: {
                    oldLayer,
                    newLayer,
                    updateBridge: this.updateBridge.bind(this),
                },
            });
        }

        this.emitFullScreenChanged(isFullScreen);
    }

    async setWatermarkFont(id: string, fontFamily: string, fontFamilyId: number, update = false) {
        const layer = this.framework.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        if (!layer) return;
        const result = await getWatermarkImg(id);
        if (!result) {
            await this.checkImageKit(layer.id);
        }
        await this.loadTextFamily(fontFamily);
        for (let i = 0; i < WATERMARK_TEXTS_MAX; i++) {
            await waterMarkBridge.setTextFont(id, fontFamily, i);
        }
        if (update) {
            await waterMarkBridge.updateResultBuffer(id);
            const newLayer = this.framework.getLayer(layer.id, true)!;
            const result = await getWatermarkImg(id);
            await waterMarkBridge.getTextsInfo(id);
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            // newLayer.materialIds =
            (newLayer.data as IWatermarkLayerProps).fontFamily = fontFamily;
            const materialIds = newLayer.materialIds || {};
            materialIds[MaterialIdsType.FONT_FAMILY] = [String(fontFamilyId)];
            newLayer.materialIds = materialIds;
            this.framework.stage.addOrUpdate(newLayer);
            this.framework.history.write({
                type: WATERMARK_HISTORY_KEY,
                data: {
                    oldLayer,
                    newLayer,
                    updateBridge: this.updateBridge.bind(this),
                },
            });
        }
        return true;
    }

    async setImageWatermarkUrl(
        layer: ILayerProps,
        url: string,
        stageSize: {
            width: number;
            height: number;
        },
        update = false,
    ) {
        const oldLayer = cloneDeep(layer);
        const newLayer = this.framework.getLayer(layer.id, true)!;
        await setImgWatermark(newLayer.id, url);
        if (update) {
            const result = (await getWatermarkImg(newLayer.id)) as {
                url: string;
                width: number;
                height: number;
            };
            (newLayer.data as IWatermarkLayerProps).url = result.url!;
            (newLayer.data as IWatermarkLayerProps).originUrl = url!;
            Object.assign(newLayer, computedPosition({ width: result.width, height: result.height }, stageSize), {
                top: newLayer.top,
                left: newLayer.left,
            });
            this.framework.stage.addOrUpdate(newLayer);
            this.framework.history.write({
                type: WATERMARK_HISTORY_KEY,
                data: {
                    oldLayer,
                    newLayer,
                    updateBridge: this.updateBridge.bind(this),
                },
            });
        }
    }

    /** 监听水印图层双击事件 */
    onDbClick(callback: (layerId: string, point: { x: number; y: number }) => void) {
        return this.framework.events.onLayerDoubleClick(callback);
    }

    /** 更新水印图层的基础属性 */
    async updateBase(newLayer: ILayerProps, oldLayer: ILayerProps) {
        const result = await getWatermarkImg(newLayer.id);
        (newLayer.data as IWatermarkLayerProps).url = result?.url!;

        this.framework.stage.addOrUpdate(newLayer);
        this.framework.history.write({
            type: WATERMARK_HISTORY_KEY,
            data: {
                oldLayer,
                newLayer,
                updateBridge: this.updateBridge.bind(this),
            },
        });
    }
    /** 获取水印图层 */
    getWatermarkLayer(index?: number) {
        const originIndex = index || this.framework.stage.getActiveOriginIndex();
        const layers = this.framework.getLayers(originIndex);

        return layers.find((layer) => layer.layerType === LayerType.WATERMARK) || null;
    }

    getInitState() {
        return this.state;
    }
    onFullScreenChanged(fn: (changed: boolean) => void) {
        return this.framework.events.on(this.fullScreenChangedEvent, fn);
    }
    emitFullScreenChanged(changed: boolean) {
        this.framework.events.emit(this.fullScreenChangedEvent, changed);
    }
    offFullScreenChanged(handler: number) {
        this.framework.events.off(handler);
    }
}

/**
 * 水印图层插件
 */
export const watermarkLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.WATERMARK,
    createInstance({ identity }) {
        return [{ key: 'WatermarkLayer', value: new WatermarkLayer(identity) }];
    },
    createState() {
        return [watermarkLayerState];
    },
    createBehavior() {
        return watermarkLayerBehavior;
    },
    createHistory({ identity }) {
        return [new WatermarkLayerHistory(identity)];
    },
    destroy({ getInstance }) {
        getInstance(WatermarkLayer).destroy();
    },
};
